import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { SAMPLE_STATUS, human, pluralizeUnit } from "@/lib/statusLabels";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Truck, CheckCheck, X, Copy } from "lucide-react";
import { SampleLine } from "@/components/samples/SampleLine";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// V4.8 RC48-SMP-008: admin-advanced sample-sourcing rail (quote/transit states are
// derived from their authoritative lifecycles and cannot be set manually)
const RAIL_STATES = [
  ["REVIEWING_AVAILABILITY", "Reviewing availability"],
  ["CHECKING_FLORIDA", "Checking Florida stock"],
  ["CHECKING_INCOMING", "Checking incoming shipments"],
  ["CHECKING_MOROCCO", "Checking Morocco supply"],
  ["PRODUCTION_REQUIRED", "Production required"],
  ["TIMING_CONFIRMED", "Timing confirmed"],
  ["QUOTE_PENDING", "Quote being prepared"],
  ["IN_PRODUCTION", "In production"],
  ["READY_TO_FULFILL", "Ready to fulfill"],
  ["UNABLE_TO_SOURCE", "Unable to source"],
];

const STATUS_BADGE = {
  AWAITING_PAYMENT: "badge-reserved",
  REQUESTED: "badge-reserved",
  PARTIALLY_SENT: "badge-reserved",
  SENT: "badge-available",
  DELIVERED: "badge-available",
  CANCELLED: "badge-neutral",
};

export default function AdminSamples() {
  const [rows, setRows] = useState(null);
  const [sentTarget, setSentTarget] = useState(null);
  const [shipForm, setShipForm] = useState({ shipment_type: "parcel", fulfillment_method: "parcel", carrier: "", service: "", tracking: "", pieces: "", estimated_delivery: "", shipping_cost: "", shipping_waived: false, lineQty: {} });
  const [chargeTarget, setChargeTarget] = useState(null);
  const [chargeForm, setChargeForm] = useState({ sample_charge_waived: false, shipping_charge_usd: "", shipping_waived: false });
  // V4.7 SAMPLE-162/164: quote builder — independent lines + waivers with reasons
  const [quoteTarget, setQuoteTarget] = useState(null);
  const [quoteForm, setQuoteForm] = useState(null);
  // V4.7 SAMPLE-165: sourcing context
  const [sourceTarget, setSourceTarget] = useState(null);
  const [sourceForm, setSourceForm] = useState({ note: "", expected_at: "" });
  // V4.8 RC48-SMP-008: sourcing sub-status rail
  const [railForm, setRailForm] = useState({});
  const [railBusy, setRailBusy] = useState("");
  const [feeCfg, setFeeCfg] = useState(null);
  const [feeSaving, setFeeSaving] = useState(false);
  const setShipField = (k) => (e) => setShipForm((f) => ({ ...f, [k]: e.target.value }));
  const [saving, setSaving] = useState(false);

  const load = () => {
    api
      .get("/admin/samples")
      .then((res) => setRows(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(load, []);

  const todayLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

  const handleMarkSent = async (e) => {
    e.preventDefault();
    if (shipForm.estimated_delivery && shipForm.estimated_delivery < todayLocal()) {
      toast.error("Estimated delivery cannot be in the past");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/samples/${sentTarget.id}/mark-sent`, {
        tracking: shipForm.tracking,
        shipment_type: "parcel",
        fulfillment_method: shipForm.fulfillment_method || "parcel",
        carrier: shipForm.carrier,
        service: shipForm.service,
        ...(shipForm.pieces ? { pieces: parseInt(shipForm.pieces, 10) } : {}),
        ...(shipForm.estimated_delivery ? { estimated_delivery: shipForm.estimated_delivery } : {}),
        ...(shipForm.shipping_cost !== "" ? { shipping_cost_usd: parseFloat(shipForm.shipping_cost) } : {}),
        shipping_waived: !!shipForm.shipping_waived,
        // §9 SAMP-031: explicit per-line quantities actually shipped (partial shipment)
        lines: sentTarget.lines
          .map((l) => ({ line_id: l.line_id, quantity: parseInt(shipForm.lineQty[l.line_id] ?? (l.quantity - (l.shipped_quantity || 0)), 10) }))
          .filter((x) => x.quantity > 0),
      });
      toast.success("Shipment recorded — allowance consumption updated for the shipped units");
      setSentTarget(null);
      setShipForm({ shipment_type: "parcel", carrier: "", service: "", tracking: "", pieces: "", estimated_delivery: "", shipping_cost: "", shipping_waived: false, lineQty: {} });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelivered = async (id) => {
    try {
      await api.post(`/admin/samples/${id}/mark-delivered`);
      toast.success("Marked delivered");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.post(`/admin/samples/${id}/cancel`, { reason: "Admin cancel" });
      toast.success("Cancelled — sample hold released and monthly allowance restored");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openCharges = (r) => {
    setChargeTarget(r);
    setChargeForm({
      sample_charge_waived: !!r.sample_charge_waived,
      shipping_charge_usd: r.shipping_charge_usd ?? "",
      shipping_waived: !!r.shipping_waived,
    });
  };

  const handleChargesSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/samples/${chargeTarget.id}/charges`, {
        sample_charge_waived: chargeForm.sample_charge_waived,
        ...(chargeForm.shipping_charge_usd !== "" ? { shipping_charge_usd: parseFloat(chargeForm.shipping_charge_usd) } : {}),
        shipping_waived: chargeForm.shipping_waived,
      });
      toast.success("Charges updated — original amounts preserved in history");
      setChargeTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openQuote = (r) => {
    setQuoteTarget(r);
    setQuoteForm({
      shipping_charge_usd: r.shipping_charge_usd ?? "",
      tariff_charge_usd: r.tariff_charge_usd ?? "",
      waive_material: false, waive_shipping: false, waive_tariff: false,
      waiver_reason_material: "", waiver_reason_shipping: "", waiver_reason_tariff: "",
    });
  };

  const handleQuote = async (send) => {
    setSaving(true);
    try {
      const res = await api.post(`/admin/samples/${quoteTarget.id}/quote`, { ...quoteForm,
        ...(quoteForm.shipping_charge_usd !== "" ? { shipping_charge_usd: parseFloat(quoteForm.shipping_charge_usd) } : {}),
        ...(quoteForm.tariff_charge_usd !== "" ? { tariff_charge_usd: parseFloat(quoteForm.tariff_charge_usd) } : {}),
        ...(quoteForm.shipping_charge_usd === "" ? { shipping_charge_usd: null } : {}),
        ...(quoteForm.tariff_charge_usd === "" ? { tariff_charge_usd: null } : {}),
        send });
      toast.success(send ? `Quote sent to dealer — total due $${(res.data.quote?.total ?? 0).toFixed(2)}. Next: dealer reviews and pays.` : "Quote draft saved");
      setQuoteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleSourcing = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/samples/${sourceTarget.id}/sourcing`, { note: sourceForm.note, ...(sourceForm.expected_at ? { expected_at: sourceForm.expected_at } : {}) });
      toast.success("Sourcing details recorded — the dealer sees Needs Sourcing with your note");
      setSourceTarget(null);
      setSourceForm({ note: "", expected_at: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-samples-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Fulfillment</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Samples</h1>
      {/* §26.2: one global Admin-configurable standard sample shipping fee */}
      <div className="mt-4 rounded-md border border-border bg-white p-4" data-testid="sample-settings-card">
        <button type="button" onClick={() => { if (!feeCfg) api.get("/admin/sample-settings").then((r) => setFeeCfg(r.data)).catch(() => {}); setFeeCfg((f) => (f ? { ...f, _open: !f._open } : { _open: true })); }}
          className="text-xs font-semibold uppercase tracking-wider text-warmgrey hover:text-terracotta" data-testid="sample-settings-toggle">
          Sample program settings
        </button>
        {feeCfg?._open && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-warmgrey">Free samples / month</Label>
              <Input type="number" min="0" className="h-10 w-32 bg-white" data-testid="sample-allowance-input"
                value={feeCfg.free_samples_per_month ?? ""} onChange={(e) => setFeeCfg((f) => ({ ...f, free_samples_per_month: parseInt(e.target.value || "0", 10) }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-warmgrey">Standard sample shipping fee ($)</Label>
              <Input type="number" min="0" step="0.01" className="h-10 w-32 bg-white" data-testid="sample-fee-input"
                value={feeCfg.additional_sample_price_usd ?? ""} onChange={(e) => setFeeCfg((f) => ({ ...f, additional_sample_price_usd: parseFloat(e.target.value || "0") }))} />
            </div>
            <Button size="sm" disabled={feeSaving} data-testid="sample-settings-save"
              className="bg-charcoal text-white hover:opacity-90"
              onClick={async () => {
                setFeeSaving(true);
                try {
                  const r = await api.put("/admin/sample-settings", {
                    free_samples_per_month: feeCfg.free_samples_per_month ?? 0,
                    additional_sample_price_usd: feeCfg.additional_sample_price_usd ?? 0,
                  });
                  setFeeCfg({ ...r.data, _open: true });
                  toast.success("Sample settings saved — applies to new requests");
                } catch { toast.error("Could not save sample settings"); }
                finally { setFeeSaving(false); }
              }}>
              {feeSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        )}
      </div>

      {!rows ? (
        <Loader2 className="mt-12 h-6 w-6 animate-spin text-terracotta" />
      ) : rows.length === 0 ? (
        <Card className="mt-12 border-border bg-white">
          <CardContent className="p-12 text-center text-sm text-muted-foreground" data-testid="admin-samples-empty">No sample requests yet.</CardContent>
        </Card>
      ) : (
        <div className="mt-10 space-y-3">
          {rows.map((r) => (
            <Card key={r.id} className="border-border bg-white" data-testid={`admin-sample-${r.id}`}>
              <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
                <div className="min-w-0">
                  <div className="space-y-1" data-testid={`admin-sample-lines-${r.id}`}>
                    {r.lines.map((l) => <SampleLine key={l.line_id} line={l} testid={`admin-sample-line-${r.id}-${l.line_id}`} />)}
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    <span className="font-medium text-charcoal">{r.dealer_name}</span>
                    {r.project_name ? ` · Project: ${r.project_name}` : ""}
                    {" · "}{r.total_quantity} unit(s) · {r.free_quantity} free
                    {r.charged_quantity > 0 ? ` · ${r.charged_quantity} charged ($${(r.charge_total ?? 0).toFixed(2)}${r.sample_charge_waived ? " — Waived" : ""})` : ""}
                    {r.shipping_charge_usd != null || r.shipping_waived ? ` · shipping ${r.shipping_charge_usd != null ? `$${(r.shipping_charge_usd ?? 0).toFixed(2)}` : ""}${r.shipping_waived ? " — Waived" : ""}` : ""}
                    {" · "}{r.is_direct_to_client ? `client: ${r.recipient?.name}, ${r.recipient?.city}` : `showroom: ${r.recipient?.city}`}
                    {" · "}{new Date(r.requested_at).toLocaleString()}
                    {r.amended_at ? ` · amended ${new Date(r.amended_at).toLocaleString()}` : ""}
                  </p>
                  {r.tracking && (
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-charcoal" data-testid={`admin-sample-tracking-${r.id}`}>
                      Tracking{r.shipment?.carrier ? ` · ${r.shipment.carrier}` : ""}: <span className="font-mono">{r.tracking}</span>
                      <button
                        type="button"
                        aria-label="Copy tracking number"
                        data-testid={`copy-tracking-${r.id}`}
                        onClick={() => { navigator.clipboard.writeText(r.tracking); toast.success("Tracking number copied"); }}
                        className="text-terracotta hover:text-terracotta-hover"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                      {r.shipment?.tracking_url && (
                        <a href={r.shipment.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`admin-track-sample-${r.id}`} className="font-semibold text-terracotta hover:text-terracotta-hover">Track →</a>
                      )}
                    </p>
                  )}
                  {r.shipment && (r.shipment.service || r.shipment.pieces || r.shipment.estimated_delivery) && (
                    <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`admin-sample-shipmeta-${r.id}`}>
                      {[
                        r.shipment.shipment_type === "freight" ? "Freight" : null,
                        r.shipment.service,
                        r.shipment.pieces ? `${r.shipment.pieces} ${r.shipment.shipment_type === "freight" ? "pallet(s)" : "package(s)"}` : null,
                        r.shipment.estimated_delivery ? `ETA ${new Date(r.shipment.estimated_delivery + "T00:00:00").toLocaleDateString()}` : null,
                      ].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`badge-status ${STATUS_BADGE[r.status]}`} data-testid={`admin-sample-status-${r.id}`}>{human(SAMPLE_STATUS, r.status)}</span>
                  {r.needs_sourcing && (
                    <span className="badge-status badge-reserved" data-testid={`sample-needs-sourcing-${r.id}`}>Needs sourcing</span>
                  )}
                  {r.payment && (
                    <span className={`badge-status ${r.payment.status === "PAID" ? "badge-available" : "badge-reserved"}`} data-testid={`sample-payment-${r.id}`}>
                      {r.payment.status === "PAID" ? `Paid $${(r.payment.amount_usd ?? 0).toFixed(2)}` : r.payment.status === "REFUNDED" ? "Refunded" : r.payment.status === "REFUND_PENDING" ? "Refund pending" : r.payment.status === "VOIDED" ? "Payment voided" : "Awaiting payment"}
                    </span>
                  )}
                  {r.custom_committed && (
                    <span className="badge-status badge-neutral" data-testid={`sample-committed-${r.id}`}>Custom — committed</span>
                  )}
                  {["REQUESTED", "QUOTE_SENT"].includes(r.status) && (
                    <Button size="sm" variant="outline" onClick={() => openQuote(r)} data-testid={`quote-edit-${r.id}`} className="border-limestone text-charcoal hover:bg-sand">
                      {r.quote?.status === "SENT" ? "Revise quote" : "Prepare quote"}
                    </Button>
                  )}
                  {["REQUESTED", "QUOTE_SENT", "AWAITING_PAYMENT", "PAID_READY"].includes(r.status) && !r.needs_sourcing && (
                    <Button size="sm" variant="outline" onClick={() => { setSourceTarget(r); setSourceForm({ note: r.sourcing_note || "", expected_at: r.sourcing_expected_at || "" }); }} data-testid={`source-sample-${r.id}`} className="border-limestone text-warmgrey hover:text-charcoal">
                      Record sourcing
                    </Button>
                  )}
                  {/* RC48-SMP-008: governed sourcing rail — admin advances, dealer sees plain-language progress */}
                  {r.needs_sourcing && r.sourcing_status && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-limestone bg-sand/50 p-2" data-testid={`sourcing-rail-${r.id}`}>
                      <span className="text-xs font-semibold text-charcoal" data-testid={`sourcing-rail-status-${r.id}`}>
                        Sourcing: {r.sourcing_status_label}
                      </span>
                      <Select value={railForm[r.id]?.status || ""} onValueChange={(v) => setRailForm((f) => ({ ...f, [r.id]: { ...(f[r.id] || {}), status: v } }))}>
                        <SelectTrigger className="h-8 w-52 bg-white" data-testid={`sourcing-rail-select-${r.id}`}><SelectValue placeholder="Advance status…" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {RAIL_STATES.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input value={railForm[r.id]?.note || ""} onChange={(e) => setRailForm((f) => ({ ...f, [r.id]: { ...(f[r.id] || {}), note: e.target.value } }))}
                        placeholder="Note to dealer (required if unable to source)" className="h-8 w-64 bg-white" data-testid={`sourcing-rail-note-${r.id}`} />
                      <Button size="sm" variant="outline" disabled={!railForm[r.id]?.status || !!railBusy}
                        onClick={async () => {
                          setRailBusy(r.id);
                          try {
                            await api.post(`/admin/samples/${r.id}/sourcing-status`, { status: railForm[r.id].status, note: railForm[r.id]?.note || "" });
                            toast.success("Sourcing status updated — dealer notified");
                            setRailForm((prev) => ({ ...prev, [r.id]: {} }));
                            load();
                          } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                          finally { setRailBusy(""); }
                        }}
                        className="border-limestone text-charcoal hover:bg-sand" data-testid={`sourcing-rail-apply-${r.id}`}>
                        {railBusy === r.id && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}Update dealer
                      </Button>
                      {!["SENT", "PARTIALLY_SENT", "DELIVERED", "CANCELLED"].includes(r.status) && (
                        <Button size="sm" variant="outline" disabled={!!railBusy || (railForm[r.id]?.note || "").trim().length < 5}
                          title="Zelligio cannot fulfil this request: closes it and fully refunds any collected payment"
                          onClick={async () => {
                            if (!window.confirm("Unable to source: close this request and fully refund any collected payment?")) return;
                            setRailBusy(r.id);
                            try {
                              await api.post(`/admin/samples/${r.id}/unable-to-source`, { reason: railForm[r.id]?.note || "" });
                              toast.success("Request closed as unable to source — refund issued where payment was collected");
                              setRailForm((prev) => ({ ...prev, [r.id]: {} }));
                              load();
                            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                            finally { setRailBusy(""); }
                          }} data-testid={`unable-to-source-${r.id}`} className="border-status-sold text-status-sold hover:bg-red-50">
                          Unable to source → refund & close
                        </Button>
                      )}
                    </div>
                  )}
                  {["REQUESTED", "PARTIALLY_SENT"].includes(r.status) && (
                    <>
                      <Button size="sm" onClick={() => setSentTarget(r)} data-testid={`mark-sent-${r.id}`} className="bg-terracotta text-white hover:bg-terracotta-hover">
                        <Truck className="mr-1 h-3.5 w-3.5" /> Mark sent
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleCancel(r.id)} data-testid={`cancel-sample-${r.id}`} className="border-limestone text-status-sold hover:bg-sand">
                        <X className="mr-1 h-3.5 w-3.5" /> Cancel
                      </Button>
                    </>
                  )}
                  {r.status === "SENT" && (
                    <Button size="sm" variant="outline" onClick={() => handleDelivered(r.id)} data-testid={`mark-delivered-${r.id}`} className="border-limestone text-charcoal hover:bg-sand">
                      <CheckCheck className="mr-1 h-3.5 w-3.5" /> Mark delivered
                    </Button>
                  )}
                  {["REQUESTED", "SENT", "PARTIALLY_SENT"].includes(r.status) && (
                    <Button size="sm" variant="ghost" onClick={() => openCharges(r)} data-testid={`charge-edit-${r.id}`} className="text-warmgrey hover:text-charcoal">
                      Charges
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!sentTarget} onOpenChange={(o) => !o && setSentTarget(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Mark as sent</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Moves units from sample reserved to shipped — the monthly allowance usage becomes final.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMarkSent} className="mt-2 space-y-4" data-testid="mark-sent-form">
            <div className="space-y-2" data-testid="ship-lines">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Units shipping now</Label>
              {(sentTarget?.lines || []).map((l) => {
                const remaining = l.quantity - (l.shipped_quantity || 0);
                return (
                  <div key={l.line_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-sand/40 px-3 py-2" data-testid={`ship-line-${l.line_id}`}>
                    <span className="min-w-0 flex-1 truncate text-xs text-charcoal"><span className="font-mono text-terracotta">{l.sku}</span> {l.variant_label}</span>
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Input type="number" min="1" max={remaining} value={shipForm.lineQty[l.line_id] ?? remaining}
                        onChange={(e) => setShipForm((f) => ({ ...f, lineQty: { ...f.lineQty, [l.line_id]: e.target.value } }))}
                        data-testid={`ship-qty-${l.line_id}`} className="h-9 w-20 bg-white text-center" />
                      of {remaining} {pluralizeUnit(l.quantity_unit, remaining)} remaining
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-sand p-1" data-testid="sample-ship-type-toggle">
              <Button type="button" data-testid="sample-ship-type-parcel" onClick={() => setShipForm((f) => ({ ...f, shipment_type: "parcel" }))}
                className={`h-9 transition-colors ${shipForm.shipment_type === "parcel" ? "bg-terracotta text-white hover:bg-terracotta" : "bg-transparent text-charcoal hover:bg-limestone"}`}>Parcel</Button>
              {/* §26.2: Freight/LTL is not a normal sample option — parcel carrier/service/tracking/package/ETA only */}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Fulfillment method</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="fulfillment-method-row">
                {[["parcel", "Parcel shipment"], ["warehouse_pickup", "Warehouse pickup"], ["personal_delivery", "Personal delivery"], ["waived_shipment", "Waived-cost shipment"]].map(([v, label]) => (
                  <Button key={v} type="button" data-testid={`fulfillment-${v}`}
                    onClick={() => setShipForm((f) => ({ ...f, fulfillment_method: v }))}
                    className={`h-9 text-xs transition-colors ${(shipForm.fulfillment_method || "parcel") === v ? "bg-terracotta text-white hover:bg-terracotta" : "bg-transparent text-charcoal border border-limestone hover:bg-sand"}`}>{label}</Button>
                ))}
              </div>
              {(shipForm.fulfillment_method || "parcel") !== "parcel" && (
                <p className="text-[11px] text-warmgrey" data-testid="fulfillment-optional-note">Carrier and tracking stay optional for this fulfillment method.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Carrier{(shipForm.fulfillment_method || "parcel") !== "parcel" ? " (optional)" : ""}</Label>
              <Input data-testid="ship-carrier" list="sample-carrier-presets" value={shipForm.carrier} onChange={setShipField("carrier")} className="h-11 bg-white focus-visible:ring-terracotta" />
              <datalist id="sample-carrier-presets">
                {["UPS", "USPS", "FedEx", "DHL", "Other"].map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Service (optional)</Label>
              <Input data-testid="ship-service" value={shipForm.service} onChange={setShipField("service")} placeholder="Ground, Priority…" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Tracking number (optional)</Label>
              <Input data-testid="tracking-input" value={shipForm.tracking} onChange={setShipField("tracking")} placeholder="Add now or share later" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Packages (optional)</Label>
                <Input type="number" min="1" data-testid="ship-pieces" value={shipForm.pieces} onChange={setShipField("pieces")} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Est. delivery (optional)</Label>
                <DatePickerField min={todayLocal()} testId="ship-eta" value={shipForm.estimated_delivery} onChange={(v) => setShipField("estimated_delivery")({ target: { value: v } })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Shipping cost $ (optional)</Label>
                <Input type="number" min="0" step="0.01" data-testid="ship-cost" value={shipForm.shipping_cost} onChange={setShipField("shipping_cost")} className="h-11 bg-white focus-visible:ring-terracotta" disabled={shipForm.shipping_waived} />
              </div>
              <label className="mt-6 flex items-center gap-2 text-xs font-medium text-charcoal" data-testid="ship-waive-row">
                <input type="checkbox" className="h-4 w-4 accent-[#BC6955]" data-testid="ship-waive" checked={shipForm.shipping_waived}
                  onChange={(e) => setShipForm((f) => ({ ...f, shipping_waived: e.target.checked }))} />
                Waive shipping charge
              </label>
            </div>
            <Button type="submit" disabled={saving} data-testid="mark-sent-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm sent"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!chargeTarget} onOpenChange={(o) => !o && setChargeTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="charge-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Sample charges</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Waivers never erase the original amount — the history keeps "$X — Waived".
            </DialogDescription>
          </DialogHeader>
          {chargeTarget && (
            <form onSubmit={handleChargesSave} className="mt-2 space-y-4" data-testid="charge-form">
              <div className="rounded-md border border-border bg-sand/50 p-3 text-sm text-charcoal">
                Sample units: {chargeTarget.charged_quantity} charged × ${(chargeTarget.unit_charge ?? 0).toFixed(2)} = <span className="font-semibold" data-testid="charge-original">${(chargeTarget.charge_total ?? 0).toFixed(2)}</span>
                {chargeTarget.sample_charge_waived && <span className="ml-1 text-status-available">— currently waived</span>}
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-charcoal" data-testid="waive-sample-charge-row">
                <input type="checkbox" className="h-4 w-4 accent-[#BC6955]" data-testid="waive-sample-charge"
                  checked={chargeForm.sample_charge_waived}
                  onChange={(e) => setChargeForm((f) => ({ ...f, sample_charge_waived: e.target.checked }))} />
                Waive sample charge (keeps ${(chargeTarget.charge_total ?? 0).toFixed(2)} in history)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Shipping charge $</Label>
                  <Input type="number" min="0" step="0.01" data-testid="charge-shipping-input" value={chargeForm.shipping_charge_usd}
                    onChange={(e) => setChargeForm((f) => ({ ...f, shipping_charge_usd: e.target.value }))}
                    className="h-11 bg-white focus-visible:ring-terracotta" disabled={chargeForm.shipping_waived} />
                </div>
                <label className="mt-6 flex items-center gap-2 text-xs font-medium text-charcoal" data-testid="waive-shipping-row">
                  <input type="checkbox" className="h-4 w-4 accent-[#BC6955]" data-testid="waive-shipping"
                    checked={chargeForm.shipping_waived}
                    onChange={(e) => setChargeForm((f) => ({ ...f, shipping_waived: e.target.checked }))} />
                  Waive shipping
                </label>
              </div>
              <Button type="submit" disabled={saving} data-testid="charge-save" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save charges"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!quoteTarget} onOpenChange={(o) => !o && setQuoteTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-lg" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Sample quote</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Independent charge lines — Sample Material, Shipping, Tariff. Waiving a line keeps its original amount and requires a reason. Sending asks the dealer to review and pay; it never marks the sample sent.
            </DialogDescription>
          </DialogHeader>
          {quoteTarget && quoteForm && (
            <div className="mt-2 space-y-4" data-testid="quote-form">
              <div className="rounded-md border border-border bg-sand/50 p-3 text-sm text-charcoal">
                Sample Material: {(quoteTarget.charge_total ?? 0) > 0 ? `$${quoteTarget.charge_total.toFixed(2)}` : "$0.00 (within free allowance)"}
              </div>
              {[
                // RC48-SMP-014/015: the material-waiver control appears only when a
                // positive material charge exists — a $0 (free allowance) request has
                // nothing to waive, so the control is hidden, not misleadingly shown
                ["material", "Waive Sample Material", "waive_material", "waiver_reason_material", quoteTarget.charge_total ?? 0],
                ["shipping", "Waive Shipping", "waive_shipping", "waiver_reason_shipping", parseFloat(quoteForm.shipping_charge_usd || "0")],
                ["tariff", "Waive Tariff", "waive_tariff", "waiver_reason_tariff", parseFloat(quoteForm.tariff_charge_usd || "0")],
              ].filter(([kind, , , , orig]) => kind !== "material" || orig > 0).map(([kind, label, flagKey, reasonKey, orig]) => (
                <div key={kind} className="rounded-md border border-border p-3" data-testid={`quote-line-${kind}`}>
                  {kind !== "material" && (
                    <div className="mb-2 flex items-center gap-2">
                      <Label className="text-xs text-warmgrey">{kind === "shipping" ? "Shipping $" : "Tariff $"}</Label>
                      <Input type="number" min="0" step="0.01" className="h-9 w-28 bg-white" data-testid={`quote-${kind}-input`}
                        value={quoteForm[`${kind}_charge_usd`]}
                        onChange={(e) => setQuoteForm((f) => ({ ...f, [`${kind}_charge_usd`]: e.target.value }))}
                        disabled={quoteForm[flagKey]} />
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm font-medium text-charcoal">
                    <input type="checkbox" className="h-4 w-4 accent-[#BC6955]" data-testid={`quote-waive-${kind}`}
                      checked={quoteForm[flagKey]}
                      onChange={(e) => setQuoteForm((f) => ({ ...f, [flagKey]: e.target.checked }))} />
                    {label}{orig > 0 ? ` ($${orig.toFixed(2)} — kept in history)` : ""}
                  </label>
                  {quoteForm[flagKey] && (
                    <Input className="mt-2 h-9 bg-white" placeholder="Reason (required to waive)" data-testid={`quote-reason-${kind}`}
                      value={quoteForm[reasonKey]}
                      onChange={(e) => setQuoteForm((f) => ({ ...f, [reasonKey]: e.target.value }))} />
                  )}
                </div>
              ))}
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={saving} onClick={() => handleQuote(false)} data-testid="quote-save-draft" className="flex-1 border-limestone">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}
                </Button>
                <Button type="button" disabled={saving} onClick={() => handleQuote(true)} data-testid="quote-send" className="flex-1 bg-terracotta text-white hover:bg-terracotta-hover">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send quote to dealer"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!sourceTarget} onOpenChange={(o) => !o && setSourceTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="sourcing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Record sourcing</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Needs Sourcing means this sample is not available for immediate fulfillment. Record timing and availability expectations for the dealer.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSourcing} className="mt-2 space-y-4" data-testid="sourcing-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Sourcing note</Label>
              <Input data-testid="sourcing-note" value={sourceForm.note} onChange={(e) => setSourceForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. Expected from Morocco container, ~3 weeks" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Expected availability (optional)</Label>
              <DatePickerField testId="sourcing-expected" value={sourceForm.expected_at} onChange={(v) => setSourceForm((f) => ({ ...f, expected_at: v }))} />
            </div>
            <Button type="submit" disabled={saving} data-testid="sourcing-save" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save sourcing details"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
